import React, { type JSX } from "react";
import {
  DocumentIcon,
  ArrowPathIcon,
  PencilIcon,
  CheckIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

import { type DocumentData, filenameOf } from "./documentPreviewTypes";
import { type DraftEditing } from "./useDraftEditing";

const SECONDARY_BUTTON =
  "px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50";

function DraftTitle({ document }: { document: DocumentData }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <DocumentIcon className="h-5 w-5 text-cvc-blue" />
      <div>
        <h3 className="text-sm font-medium text-gray-900">{filenameOf(document)}</h3>
        {document.updatedAt && (
          <p className="text-xs text-gray-500">
            Last updated: {new Date(document.updatedAt).toLocaleString()}
            {document.lastUpdatedBy && ` by ${document.lastUpdatedBy}`}
          </p>
        )}
      </div>
    </div>
  );
}

function SaveButtonLabel({ saving }: { saving: boolean }): JSX.Element {
  if (saving) {
    return (
      <>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
        Saving...
      </>
    );
  }
  return (
    <>
      <CheckIcon className="h-4 w-4 mr-1" />
      Save
    </>
  );
}

/** Save / cancel / preview toggle, shown only while the draft is being edited. */
function EditingControls({ draft }: { draft: DraftEditing }): JSX.Element {
  return (
    <>
      {draft.isDirty && <span className="text-xs text-orange-600 font-medium">Unsaved changes</span>}
      {draft.showPreview ? (
        <button onClick={() => draft.setShowPreview(false)} className={SECONDARY_BUTTON}>
          Edit
        </button>
      ) : (
        <button onClick={() => draft.setShowPreview(true)} className={SECONDARY_BUTTON}>
          <EyeIcon className="h-4 w-4 inline mr-1" />
          Preview
        </button>
      )}
      <button
        onClick={draft.cancelEditing}
        disabled={draft.saving}
        className={`${SECONDARY_BUTTON} disabled:opacity-50`}
      >
        Cancel
      </button>
      <button
        onClick={() => void draft.saveDraft()}
        disabled={!draft.isDirty || draft.saving}
        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <SaveButtonLabel saving={draft.saving} />
      </button>
    </>
  );
}

export interface DraftHeaderBarProps {
  document: DocumentData;
  draft: DraftEditing;
  /** True for the generated outline/metadata/content files, which are read-only. */
  isGeneratedFile: boolean;
  documentType: string;
  loading: boolean;
  onRefresh: () => void;
}

/** Title bar and editing controls for a doc-drafter draft. */
export default function DraftHeaderBar({
  document,
  draft,
  isGeneratedFile,
  documentType,
  loading,
  onRefresh,
}: DraftHeaderBarProps): JSX.Element {
  // draft_content is edited through checkbox selection rather than the text editor
  const canEdit = documentType !== "draft_content" && !isGeneratedFile;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
      <DraftTitle document={document} />
      <div className="flex items-center gap-2">
        {!isGeneratedFile && (
          <button
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh document"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        )}
        {canEdit && draft.isEditing && <EditingControls draft={draft} />}
        {canEdit && !draft.isEditing && (
          <button
            onClick={() => void draft.startEditing()}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-cvc-blue border border-transparent rounded-md hover:bg-cvc-marine"
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit Draft
          </button>
        )}
      </div>
    </div>
  );
}
